export type MealCategory = 'breakfast' | 'lunch' | 'dinner' | 'drinks';

export type MenuTag = 'popular' | 'vegetarian' | 'spicy' | 'new';

export interface MenuItem {
  id: string;
  name: string;
  description: string;
  /** Price in Kenyan Shillings */
  price: number;
  image: string;
  category: MealCategory;
  tags: MenuTag[];
  prepMinutes: number;
}

const img = (id: string) => `https://images.unsplash.com/${id}?auto=format&fit=crop&w=800&q=70`;

export const MENU: MenuItem[] = [
  // ---------------------------------------------------------------- Breakfast
  {
    id: 'mandazi-chai',
    name: 'Mandazi & Chai',
    description: 'Soft, pillowy mandazi (4 pc) paired with a steaming cup of spiced Kenyan chai.',
    price: 120,
    image: img('photo-1568254183919-78a4f43a2877'),
    category: 'breakfast',
    tags: ['popular', 'vegetarian'],
    prepMinutes: 10,
  },
  {
    id: 'uji-power',
    name: 'Uji Power',
    description: 'Warm millet porridge with groundnuts, wild honey and a squeeze of lime.',
    price: 150,
    image: img('photo-1546069901-ba9599a7e63c'),
    category: 'breakfast',
    tags: ['vegetarian'],
    prepMinutes: 8,
  },
  {
    id: 'big-breakfast',
    name: 'MLO Big Breakfast',
    description: 'Two farm eggs any style, beef sausage, grilled tomato, toast and seasonal fruit.',
    price: 480,
    image: img('photo-1525351484163-7529414344d8'),
    category: 'breakfast',
    tags: ['popular'],
    prepMinutes: 15,
  },
  {
    id: 'beef-samosa',
    name: 'Beef Samosa (2 pc)',
    description: 'Crispy golden pastry pockets filled with spiced minced beef and fresh coriander.',
    price: 100,
    image: img('photo-1601050690597-df0568f70950'),
    category: 'breakfast',
    tags: ['spicy'],
    prepMinutes: 8,
  },
  {
    id: 'mahamri-mbaazi',
    name: 'Mahamri & Mbaazi',
    description: 'Coastal coconut mahamri (3 pc) served with mbaazi simmered in creamy coconut.',
    price: 200,
    image: img('photo-1484723091739-30a097e8f929'),
    category: 'breakfast',
    tags: ['vegetarian'],
    prepMinutes: 12,
  },
  {
    id: 'banana-pancakes',
    name: 'Banana Pancakes & Honey',
    description: 'Fluffy ripe-banana pancakes drizzled with wild honey and a knob of butter.',
    price: 250,
    image: img('photo-1567620905732-2d1ec7ab7445'),
    category: 'breakfast',
    tags: ['vegetarian', 'new'],
    prepMinutes: 12,
  },
  {
    id: 'chapati-egg-roll',
    name: 'Chapati Egg Roll (Rolex)',
    description: 'Soft flaky chapati rolled with a fluffy omelette, tomato and red onion. Street classic.',
    price: 200,
    image: img('photo-1608198093002-ad4e005484ec'),
    category: 'breakfast',
    tags: ['popular'],
    prepMinutes: 10,
  },

  // -------------------------------------------------------------------- Lunch
  {
    id: 'ugali-nyama',
    name: 'Ugali, Sukuma & Nyama Choma',
    description: "Kenya's favourite plate: chargrilled beef, hot ugali and garlicky sukuma wiki.",
    price: 450,
    image: img('photo-1555939594-58d7cb561ad1'),
    category: 'lunch',
    tags: ['popular'],
    prepMinutes: 20,
  },
  {
    id: 'chicken-pilau',
    name: 'Chicken Pilau',
    description: 'Coastal-style fragrant pilau rice with fall-off-the-bone chicken and kachumbari.',
    price: 380,
    image: img('photo-1574484284002-952d92456975'),
    category: 'lunch',
    tags: ['spicy', 'popular'],
    prepMinutes: 18,
  },
  {
    id: 'githeri-special',
    name: 'Githeri Special',
    description: 'Slow-simmered maize & beans with potatoes, crowned with creamy fresh avocado.',
    price: 280,
    image: img('photo-1547592180-85f173990554'),
    category: 'lunch',
    tags: ['vegetarian'],
    prepMinutes: 15,
  },
  {
    id: 'mukimo-beef',
    name: 'Mukimo & Beef Stew',
    description: 'Central Kenya classic: silky mash of potatoes, peas & maize with rich beef stew.',
    price: 350,
    image: img('photo-1546833999-b9f581a1996d'),
    category: 'lunch',
    tags: [],
    prepMinutes: 18,
  },
  {
    id: 'tilapia-ugali',
    name: 'Tilapia Fry & Ugali',
    description: 'Whole Lake Victoria tilapia fried crisp, with ugali, sukuma wiki and kachumbari.',
    price: 550,
    image: img('photo-1559847844-5315695dadae'),
    category: 'lunch',
    tags: ['popular'],
    prepMinutes: 22,
  },
  {
    id: 'beef-stew-rice',
    name: 'Beef Stew & Rice',
    description: 'Tender beef slow-cooked in tomato, garlic and pilipili, ladled over steamed rice.',
    price: 320,
    image: img('photo-1512058564366-18510be2db19'),
    category: 'lunch',
    tags: [],
    prepMinutes: 15,
  },
  {
    id: 'chapati-ndengu',
    name: 'Chapati & Ndengu',
    description: 'Flaky layered chapati (2 pc) with buttery green-gram curry and a tomato salsa.',
    price: 300,
    image: img('photo-1504674900247-0877df9cc836'),
    category: 'lunch',
    tags: ['vegetarian'],
    prepMinutes: 15,
  },

  // ------------------------------------------------------------------- Dinner
  {
    id: 'nyama-choma-platter',
    name: 'Nyama Choma Platter (½ kg)',
    description: 'Goat meat roasted slow over the charcoal jiko, with ugali and fiery kachumbari.',
    price: 850,
    image: img('photo-1544025162-d76694265947'),
    category: 'dinner',
    tags: ['popular'],
    prepMinutes: 25,
  },
  {
    id: 'kienyeji-chicken',
    name: 'Kienyeji Chicken Wet Fry',
    description: 'Free-range kienyeji chicken simmered in a rich tomato, garlic & pilipili sauce.',
    price: 650,
    image: img('photo-1565557623262-b51c2513a641'),
    category: 'dinner',
    tags: ['spicy'],
    prepMinutes: 25,
  },
  {
    id: 'mbuzi-fry',
    name: 'Mbuzi Fry & Ugali',
    description: 'Dry-fried goat meat tossed with onions, dhania and green pepper. Ugali on the side.',
    price: 520,
    image: img('photo-1519708227418-c8fd9a32b7a2'),
    category: 'dinner',
    tags: [],
    prepMinutes: 20,
  },
  {
    id: 'chips-masala',
    name: 'Chips Masala',
    description: 'Crispy chips tossed in a fiery masala sauce — Nairobi street-style, extra chilli on request.',
    price: 350,
    image: img('photo-1541592106381-b31e9677c0e5'),
    category: 'dinner',
    tags: ['spicy', 'vegetarian'],
    prepMinutes: 12,
  },
  {
    id: 'mutura-kachumbari',
    name: 'Mutura & Kachumbari',
    description: 'Two slices of smoky grilled mutura with fresh kachumbari and a pinch of salt & chilli.',
    price: 220,
    image: img('photo-1562967914-608f82629710'),
    category: 'dinner',
    tags: ['popular', 'spicy'],
    prepMinutes: 10,
  },
  {
    id: 'mlo-burger',
    name: 'MLO Beef Burger & Fries',
    description: 'Flame-grilled beef patty, house sauce and crunchy fries. Built for late-night cravings.',
    price: 480,
    image: img('photo-1571091718767-18b5b1457add'),
    category: 'dinner',
    tags: ['new'],
    prepMinutes: 15,
  },
  {
    id: 'fish-wet-fry',
    name: 'Whole Fish Wet Fry',
    description: 'Whole tilapia simmered in a tomato-coconut wet fry, served with ugali and managu.',
    price: 700,
    image: img('photo-1600891964092-4316c288032e'),
    category: 'dinner',
    tags: [],
    prepMinutes: 25,
  },

  // ------------------------------------------------------------------- Drinks
  {
    id: 'kenyan-chai',
    name: 'Kenyan Chai',
    description: 'Milky tea brewed strong with a whisper of ginger — the true taste of home.',
    price: 80,
    image: img('photo-1544787219-7f47ccb76574'),
    category: 'drinks',
    tags: ['popular', 'vegetarian'],
    prepMinutes: 5,
  },
  {
    id: 'chai-masala',
    name: 'Chai Masala',
    description: 'Tea simmered with cardamom, cinnamon, cloves and ginger. Warming and bold.',
    price: 100,
    image: img('photo-1564890369478-c89ca6d9cde9'),
    category: 'drinks',
    tags: ['vegetarian'],
    prepMinutes: 6,
  },
  {
    id: 'kahawa',
    name: 'Kahawa (Kenyan AA)',
    description: 'Freshly brewed single-origin Kenyan AA coffee — bright, fruity and full-bodied.',
    price: 120,
    image: img('photo-1495474472287-4d71bcdd2085'),
    category: 'drinks',
    tags: ['vegetarian'],
    prepMinutes: 5,
  },
  {
    id: 'iced-kahawa',
    name: 'Iced Kahawa',
    description: 'Cold-brewed Kenyan coffee over ice with a touch of sugar syrup. Nairobi heat approved.',
    price: 150,
    image: img('photo-1550617931-e17a7b70dce2'),
    category: 'drinks',
    tags: ['vegetarian', 'new'],
    prepMinutes: 5,
  },
  {
    id: 'passion-juice',
    name: 'Fresh Passion Juice',
    description: 'Hand-pressed passion fruit juice — sweet, tangy and straight from the market.',
    price: 150,
    image: img('photo-1600271886742-f049cd451bba'),
    category: 'drinks',
    tags: ['vegetarian'],
    prepMinutes: 5,
  },
  {
    id: 'kahawa-break',
    name: 'Kahawa & Mandazi Break',
    description: 'A proper Kenyan chai-break: hot AA coffee with two soft mandazi on the side.',
    price: 180,
    image: img('photo-1541167760496-1628856ab772'),
    category: 'drinks',
    tags: ['popular'],
    prepMinutes: 6,
  },
];

export const CATEGORY_META: Record<MealCategory, { label: string; swahili: string; hours: string }> = {
  breakfast: { label: 'Breakfast', swahili: 'Kifungua kinywa', hours: '5:00 – 11:00 AM' },
  lunch: { label: 'Lunch', swahili: 'Chakula cha mchana', hours: '11:00 AM – 4:00 PM' },
  dinner: { label: 'Dinner', swahili: 'Chakula cha jioni', hours: '4:00 – 10:00 PM' },
  drinks: { label: 'Drinks', swahili: 'Vinywaji', hours: 'All day' },
};

export function getItemsByCategory(category: MealCategory): MenuItem[] {
  return MENU.filter((item) => item.category === category);
}

export function getMenuItem(id: string): MenuItem | undefined {
  return MENU.find((item) => item.id === id);
}

/** The big hero photograph — a charcoal grill in full swing. */
export const HERO_IMAGE = `https://images.unsplash.com/photo-1555939594-58d7cb561ad1?auto=format&fit=crop&w=1600&q=75`;
